import type { Condition } from '@locus-taxy/encore-grid';
import type { Navigation, NavigationItem } from '../types/navigation';

/**
 * Stable identity for one nav count badge (DP-6112).
 *
 * The nav tree is built from the modules config alone and the counts arrive
 * later over their own queries, so the two sides need a join key that both can
 * derive from the same wire fields without knowing about each other. That key
 * is what `NavigationPageItem.badgeKey` carries; `applyNavBadges` resolves it
 * to `badge` once the aggregates land.
 *
 * Entity name alone is not enough — the same entity can appear twice in the
 * nav under different `countCondition`s (say Unread and All), and those are two
 * different numbers. The condition object comes straight off the wire on both
 * sides, so its key order is identical and `JSON.stringify` is stable here.
 */
export function navBadgeKey(
  entityName: string,
  countCondition?: Condition
): string {
  return countCondition
    ? `${entityName}|${JSON.stringify(countCondition)}`
    : entityName;
}

function applyToItem(
  item: NavigationItem,
  counts: Record<string, number>
): NavigationItem {
  if (item.kind === 'header' || item.kind === 'divider') {
    return item;
  }
  if (item.children) {
    return { ...item, children: applyNavBadges(item.children, counts) };
  }
  const count = item.badgeKey ? counts[item.badgeKey] : undefined;
  return count === undefined ? item : { ...item, badge: count };
}

/**
 * Resolve every `badgeKey` in a navigation tree against the fetched counts.
 *
 * A leaf whose count has not landed (still loading, or the request failed) is
 * left untouched rather than badged with a placeholder — a nav badge that
 * flickers a zero on every page load reads as real data.
 */
export function applyNavBadges(
  navigation: Navigation,
  counts: Record<string, number>
): Navigation {
  if (Object.keys(counts).length === 0) {
    return navigation;
  }
  return navigation.map(item => applyToItem(item, counts));
}
